/**
 * ================================================================================================
 *  THE PUBLIC PAGE. **Section 8, 2026-10-27.**
 * ================================================================================================
 *
 * Owner:
 *
 *   > *"דף ציבורי שיווקי: הסבר קצר על GL-POINT, צילומי מערכת ללא מידע פרטי, וכפתורי „הרשמה”
 *   > ו„כניסה”."*
 *   > *"ניסיון חינם: מספר הימים נלקח מהמדיניות שאני מגדיר בקונסולת הבעלים. ההצעה באתר והאכיפה
 *   > בהרשמה חייבות להשתמש באותה מדיניות."*
 *   > *"כפתור הרשמה מיועד להרשמה עצמית. כל עוד המסלול אינו ממומש, אל תפנה לטופס קשר כאילו זו
 *   > הרשמה עובדת ואל תציג הבטחת ניסיון שאי אפשר לממש."*
 *   > *"הכן אותו באותה אפליקציה, ללא צורך בהקמת אתר ותשתית נוספים."*
 *
 * ── WHAT IS HERE, AND THE TWO THINGS THAT ARE DELIBERATELY NOT ──────────────────────────────────
 *
 * **NO NUMBER OF TRIAL DAYS.** The policy is real and the Owner sets it —
 * `platform.policy.trial_days.set`, bounded by `TRIAL_DAYS_MIN`/`TRIAL_DAYS_MAX` in
 * `apps/api/src/services/commercial-policy.ts` — but reading it requires
 * `platform.subscription.edit`, and nobody reading this page has a session at all. A number written
 * here would be a SECOND copy of a commercial policy, in the one place no server can correct it, and
 * the first time the Owner changed the policy the page would be advertising the old one. When
 * self-registration exists, the offer and the enforcement read the same row through a public
 * endpoint; until then this page offers no day count, which is the Owner's own rule applied to his
 * own sentence.
 *
 * **NO SIGN-UP THAT IS NOT A SIGN-UP.** Self-registration is not built. The page says so and gives
 * the one address that actually reaches a person, rather than a button that opens a contact form
 * dressed as an account.
 *
 * ── AND NO SCREENSHOTS ──────────────────────────────────────────────────────────────────────────
 *
 * *"צילומי מערכת ללא מידע פרטי"* — the Owner asked for product screenshots on the page. There is no
 * screenshot in this repository that is free of a customer's data: every image of this product so
 * far is of an estate with a real office name in the header. An invented screenshot of a product
 * that does not look like that would be the other kind of lie. The page is built to take them —
 * `.welcome__shots` is the slot — and they are an Owner-supplied asset, named in the delivery as an
 * open item rather than faked here.
 */

import { useCallback } from 'react';

import { routes } from '../routes';
import { callOperation } from '../api/operations';
import { useServerData } from '../api/useServerData';
import { useLocale } from '../i18n/LocaleProvider';
import { DIRECTION } from '../i18n/catalogue';
import logoUrl from '../assets/glpoint-logo.jpeg';

export function WelcomeScreen(): JSX.Element {
  const { t, locale } = useLocale();

  /*
   * ── THE DOOR, READ FROM THE SERVER, THROUGH THE ONE QUESTION THAT CANNOT LIE ─────────────────
   *
   * ו-16: *"הדף הציבורי יציג כניסה והרשמה. כשההרשמה סגורה, הצג הודעה קצרה וברורה שהיא אינה
   * זמינה כרגע, בלי כפתור שמוביל למבוי סתום."*
   *
   * The condition is NOT "is the door flag true". It is **"is there a plan a stranger can
   * actually buy"**, because those are different states and only the second one makes the button
   * work. `self_signup_open = true` with no plan marked `is_public_signup` is a door that opens
   * onto a form with nothing to choose — which is the dead end the instruction names, reached by
   * a button that looked fine.
   *
   * `public.signup.plans` answers both halves in one read: it is 404 while the door is shut, and
   * `{ plans: [] }` while the door is open and nothing is on offer. So no new route, no second
   * place the door is decided, and no flag this page could read as open while the next page
   * refuses.
   *
   * AND THE DEFAULT IS CLOSED. While the read is in flight, and if it fails for any other reason,
   * no action renders. A landing page that offers a button optimistically and withdraws it a
   * moment later has shown a stranger a door that was not there.
   */
  const readPlans = useCallback(() => callOperation('public.signup.plans', {}), []);
  const offers = useServerData(readPlans);
  const open = offers.result.state === 'loaded' && offers.result.data.plans.length > 0;
  const settled = offers.result.state !== 'loading';

  return (
    <main className="welcome" lang={locale} dir={DIRECTION[locale]}>
      <header className="welcome__head">
        {/* The mark IS the name, so the alternative text is the name and not the word "logo". */}
        <img className="welcome__logo" src={logoUrl} alt="GL-POINT" />
        <div className="welcome__actions">
          <a className="btn btn--enter" href={routes.signIn}>
            {t('welcome.signIn')}
          </a>
        </div>
      </header>

      <section className="welcome__lead">
        <h1>{t('welcome.title')}</h1>
        <p>{t('welcome.lead')}</p>
      </section>

      <section className="welcome__points">
        <article>
          <h2>{t('welcome.point1Title')}</h2>
          <p>{t('welcome.point1Body')}</p>
        </article>
        <article>
          <h2>{t('welcome.point2Title')}</h2>
          <p>{t('welcome.point2Body')}</p>
        </article>
        <article>
          <h2>{t('welcome.point3Title')}</h2>
          <p>{t('welcome.point3Body')}</p>
        </article>
      </section>

      {/*
        THE SIGN-UP SLOT. This said a button "would open nothing: the self-registration route does
        not exist" — true when it was written, and false since §10.2 built `/signup`, which reads
        the price book, mails a verification and creates nothing until the link is opened.

        Three states, and the quiet one is deliberate: nothing renders until the read settles, so
        the page never shows an action it then takes away.
      */}
      <section className="welcome__signup">
        <h2>{t('welcome.signUpTitle')}</h2>
        {!settled ? null : open ? (
          <>
            <p>{t('welcome.signUpOpenBody')}</p>
            {/*
              `btn--nav` AND NOT `btn--primary`. This is a DESTINATION, and
              `components/actionAffordance.test.ts` rule 2 refuses a link wearing the treatment a
              write wears -- which it caught here on the first run. The vocabulary is in styles.css:
              filled navy is a write, teal outline is a destination. Registering is a page away;
              nothing is created by arriving.
            */}
            <p>
              <a className="btn btn--nav" href={routes.publicSignup}>
                {t('welcome.signUpAction')}
              </a>
            </p>
          </>
        ) : (
          <p>{t('welcome.signUpBody')}</p>
        )}
      </section>

      <footer className="welcome__foot">
        {/*
          THE PRODUCT NAME IS NOT A TRANSLATION. It was a catalogue entry reading `GL-POINT` in
          both languages, and `identity-language.test.ts` refused it — correctly: *"the Hebrew is
          Hebrew and the English is not"* exists so that a label nobody translated cannot sit in
          the catalogue looking translated. A name is the same in every language, so it is written
          here, like the `alt` on the mark above.
        */}
        <p>GL-POINT</p>
      </footer>
    </main>
  );
}
